// src/pages/fullServiceInvoice/component/FullServiceKmFormDialog.jsx
import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import ButtonGroup from '../../../../components/ButtonGroup/ButtonGroup';

const initialState = {
    id: undefined,
    contractStartDate: null,
    contractEndDate: null,
    km: null,
    price: null,
    currency: '',
    description: '',
    status: 10,
    dealerAgreement: 0
};

export const FullServiceKmStatus = {
    Draft: 10,
    WaitingApproval: 20,
    Returned: 25,
    Rejected: 30,
    Accepted: 40
};

const overlayStyle = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255,255,255,0.6)',
    zIndex: 2
};

const FullServiceKmFormDialog = ({
    isCanUseFullService,
    isWarranty,
    invoiceDetail,
    visible,
    onHide,
    onSubmit, // Kaydet (toast + close + refetch)
    onUpsertSilent, // Sessiz upsert (close/toast yok)
    onChangeStatus,
    invoiceId,
    initialData,
    loading
}) => {
    const { th } = useLanguage();
    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const isProcessing = !!loading;

    const isEdit = Boolean(initialData?.id);

    // Reason dialog (inline)
    const [reasonDlg, setReasonDlg] = useState({
        visible: false,
        nextStatus: null,
        type: 'return', // 'return' | 'reject'
        text: '',
        submitted: false
    });

    const openReasonDialog = (nextStatus, type /* 'return' | 'reject' */) => {
        if (isProcessing) return;
        setReasonDlg({
            visible: true,
            nextStatus,
            type: type === 'reject' ? 'reject' : 'return',
            text: '',
            submitted: false
        });
    };

    const REQUIRES_SAVE_BEFORE = new Set([FullServiceKmStatus.WaitingApproval, FullServiceKmStatus.Accepted]);

    useEffect(() => {
        if (!visible) return;
        if (initialData) {
            setFormData({
                ...initialState,
                ...initialData,
                currency: initialData.currency ?? invoiceDetail?.currency ?? '',
                dealerAgreement: Number(initialData?.dealerAgreement ?? 0)
            });
        } else {
            setFormData({
                ...initialState,
                currency: invoiceDetail?.currency || '',
                dealerAgreement: Number(invoiceDetail?.dealerAgreement ?? 0)
            });
        }
        setErrors({});
        setReasonDlg({ visible: false, nextStatus: null, type: 'return', text: '', submitted: false });
    }, [visible, initialData, invoiceDetail]);

    const currentStatus = Number(formData?.status ?? FullServiceKmStatus.Draft);
    const isDraft = currentStatus === FullServiceKmStatus.Draft;
    const isWaitingApproval = currentStatus === FullServiceKmStatus.WaitingApproval;
    const isReturned = currentStatus === FullServiceKmStatus.Returned;
    const isRejected = currentStatus === FullServiceKmStatus.Rejected;
    const isAccepted = currentStatus === FullServiceKmStatus.Accepted;

    // === INPUT DISABLE KURALI ===
    // Edit modda:
    // - Rejected / WaitingApproval / Accepted  -> KMobility & Warranty: disabled
    // - Draft / Returned                       -> Warranty: disabled, KMobility: editable
    // + isProcessing her durumda disabled
    const mustDisableByStatus = isEdit && (isRejected || isWaitingApproval || isAccepted || (isWarranty && (isDraft || isReturned)));

    const inputDisabled = isProcessing || mustDisableByStatus;

    const validate = () => {
        const errs = {};
        if (!formData.contractStartDate) errs.contractStartDate = th('fullServiceInvoiceKm.validation.contractStartDateRequired');
        if (!formData.contractEndDate) errs.contractEndDate = th('fullServiceInvoiceKm.validation.contractEndDateRequired');

        if (formData.km === null || formData.km < 0) errs.km = th('fullServiceInvoiceKm.validation.kmRequired');

        // if (isWarranty) {
        //     if (formData.price === null || formData.price < 0) errs.price = th('fullServiceInvoiceKm.validation.priceRequired');
        // }
        if (!formData.currency) errs.currency = th('fullServiceInvoiceKm.validation.currencyRequired');

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onKmChange = (kmVal) => {
        const km = kmVal ?? 0;
        const unit = Number(invoiceDetail?.agreementAmount) || 0;
        setFormData((prev) => ({
            ...prev,
            km,
            price: unit * (Number(km) || 0)
        }));
    };

    const handleSave = async () => {
        if (isProcessing) return;
        if (!validate()) return;

        const payload = {
            ...formData,
            fullServiceId: Number(invoiceId),
            bosBranchCode: invoiceDetail?.bosBranchCode || '',
            bosBranchSiteCode: invoiceDetail?.bosBranchSiteCode || ''
        };
        await onSubmit?.(payload);
    };

    const doChangeStatus = async (nextStatus, reason) => {
        if (isProcessing) return;

        if (REQUIRES_SAVE_BEFORE.has(nextStatus)) {
            if (!validate()) return;
            const upsertPayload = {
                ...formData,
                fullServiceId: Number(invoiceId),
                bosBranchCode: invoiceDetail?.bosBranchCode || '',
                bosBranchSiteCode: invoiceDetail?.bosBranchSiteCode || ''
            };
            await onUpsertSilent?.(upsertPayload);
        }

        await onChangeStatus?.({
            id: formData.id,
            nextStatus,
            reason: reason?.trim() || undefined
        });
    };

    // ReasonDialog i18n cfg
    const cfgMap = {
        reject: {
            title: th('fullServiceInvoiceKm.reasonDialog.reject.title'),
            label: th('fullServiceInvoiceKm.reasonDialog.reject.label'),
            confirmButton: th('fullServiceInvoiceKm.reasonDialog.reject.confirmButton'),
            minLength: 20,
            minLengthMessage: th('fullServiceInvoiceKm.reasonDialog.reject.minLengthMessage'),
            requiredMessage: th('fullServiceInvoiceKm.reasonDialog.reject.requiredMessage'),
            placeholder: th('fullServiceInvoiceKm.reasonDialog.reject.placeholder'),
            minLengthText: th('fullServiceInvoiceKm.reasonDialog.reject.minLengthText')
        },
        return: {
            title: th('fullServiceInvoiceKm.reasonDialog.return.title'),
            label: th('fullServiceInvoiceKm.reasonDialog.return.label'),
            confirmButton: th('fullServiceInvoiceKm.reasonDialog.return.confirmButton'),
            minLength: 20,
            minLengthMessage: th('fullServiceInvoiceKm.reasonDialog.return.minLengthMessage'),
            requiredMessage: th('fullServiceInvoiceKm.reasonDialog.return.requiredMessage'),
            placeholder: th('fullServiceInvoiceKm.reasonDialog.return.placeholder'),
            minLengthText: th('fullServiceInvoiceKm.reasonDialog.return.minLengthText')
        }
    };
    const cfg = cfgMap[reasonDlg.type] || cfgMap.reject;
    const characterCountText = th('fullServiceInvoiceKm.reasonDialog.characterCount')
        .replace('{0}', reasonDlg.text ? String(reasonDlg.text.length) : '0')
        .replace('{1}', String(cfg.minLength));

    const handleReasonConfirm = async () => {
        const text = reasonDlg.text?.trim() || '';
        setReasonDlg((p) => ({ ...p, submitted: true }));
        if (!text || text.length < cfg.minLength) return;

        await doChangeStatus(reasonDlg.nextStatus, text);
        setReasonDlg({ visible: false, nextStatus: null, type: 'return', text: '', submitted: false });
        onHide?.();
    };
    const handleReasonCancel = () => {
        setReasonDlg({ visible: false, nextStatus: null, type: 'return', text: '', submitted: false });
    };

    const getActionButtons = () => {
        const buttons = [];

        // === KMobility ===
        if (isCanUseFullService && !isWarranty) {
            if (!isEdit) {
                buttons.push({
                    label: th('fullServiceInvoiceKm.buttons.save'),
                    icon: 'pi pi-save',
                    onClick: handleSave,
                    severity: 'gray',
                    disabled: isProcessing
                });
                return buttons;
            }
            if (isDraft || isReturned) {
                buttons.push(
                    {
                        label: th('fullServiceInvoiceKm.buttons.save'),
                        icon: 'pi pi-save',
                        onClick: handleSave,
                        severity: 'gray',
                        disabled: isProcessing
                    },
                    {
                        label: th('fullServiceInvoiceKm.buttons.saveAndSend'),
                        icon: 'pi pi-send',
                        onClick: async () => {
                            await doChangeStatus(FullServiceKmStatus.WaitingApproval);
                            onHide?.();
                        },
                        disabled: isProcessing
                    }
                );
            }
            return buttons;
        }

        // === Warranty ===
        if (isWarranty) {
            if (!isEdit) {
                buttons.push({
                    label: th('fullServiceInvoiceKm.buttons.saveAndSendToSap'),
                    icon: 'pi pi-save',
                    onClick: handleSave,
                    severity: 'gray',
                    disabled: isProcessing
                });
                return buttons;
            }
            if (isWaitingApproval) {
                buttons.push(
                    {
                        label: th('fullServiceInvoiceKm.buttons.approveAndSend'),
                        icon: 'pi pi-check',
                        onClick: async () => {
                            await doChangeStatus(FullServiceKmStatus.Accepted);
                            onHide?.();
                        },
                        disabled: isProcessing
                    },
                    {
                        label: th('fullServiceInvoiceKm.buttons.return'),
                        icon: 'pi pi-replay',
                        onClick: () => openReasonDialog(FullServiceKmStatus.Returned, 'return'),
                        severity: 'gray',
                        disabled: isProcessing
                    },
                    {
                        label: th('fullServiceInvoiceKm.buttons.reject'),
                        icon: 'pi pi-times',
                        onClick: () => openReasonDialog(FullServiceKmStatus.Rejected, 'reject'),
                        severity: 'danger',
                        disabled: isProcessing
                    }
                );
            }
            return buttons;
        }

        return buttons;
    };

    return (
        <>
            {/* Ana form diyaloğu */}
            <Dialog
                visible={visible}
                header={initialData ? th('fullServiceInvoiceKm.dialogHeader.edit') : th('fullServiceInvoiceKm.dialogHeader.create')}
                onHide={onHide}
                modal
                className="p-fluid"
                style={{ width: '640px' }}
                closable={!isProcessing}
                dismissableMask={false}
                footer={
                    <div className="flex justify-content-between w-full">
                        {/* SOL: Çık */}
                        <ButtonGroup
                            buttons={[
                                {
                                    label: th('fullServiceInvoiceKm.buttons.exit'),
                                    icon: 'pi pi-times',
                                    onClick: onHide,
                                    severity: 'gray',
                                    disabled: isProcessing
                                }
                            ]}
                        />
                        {/* SAĞ: Aksiyonlar */}
                        <ButtonGroup buttons={getActionButtons()} />
                    </div>
                }
            >
                <div style={{ position: 'relative' }}>
                    {isProcessing && (
                        <div style={overlayStyle}>
                            <ProgressSpinner />
                        </div>
                    )}

                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.contractStartDate')} *</label>
                            <Calendar
                                value={formData.contractStartDate ? new Date(formData.contractStartDate) : null}
                                onChange={(e) => setFormData({ ...formData, contractStartDate: e.value })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                className={errors.contractStartDate ? 'p-invalid' : ''}
                                placeholder={th('fullServiceInvoiceKm.placeholders.contractStartDate')}
                                disabled={inputDisabled}
                            />
                            {errors.contractStartDate && <small className="p-error block mt-1">{errors.contractStartDate}</small>}
                        </div>

                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.contractEndDate')}</label>
                            <Calendar
                                value={formData.contractEndDate ? new Date(formData.contractEndDate) : null}
                                onChange={(e) => setFormData({ ...formData, contractEndDate: e.value })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                placeholder={th('fullServiceInvoiceKm.placeholders.contractEndDate')}
                                disabled={inputDisabled}
                            />
                            {errors.contractEndDate && <small className="p-error block mt-1">{errors.contractEndDate}</small>}
                        </div>

                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.km')} *</label>
                            <InputNumber value={formData.km} onValueChange={(e) => onKmChange(e.value)} min={0} className={errors.km ? 'p-invalid' : ''} placeholder={th('fullServiceInvoiceKm.placeholders.km')} disabled={inputDisabled} />
                            {errors.km && <small className="p-error block mt-1">{errors.km}</small>}
                        </div>

                        {/* Price sadece Warranty için görünür */}
                        {isWarranty && isEdit && (
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.price')} *</label>
                                <InputNumber
                                    value={formData.price}
                                    min={0}
                                    mode="decimal"
                                    minFractionDigits={2}
                                    maxFractionDigits={4}
                                    className={errors.price ? 'p-invalid' : ''}
                                    placeholder={th('fullServiceInvoiceKm.placeholders.price')}
                                    disabled={inputDisabled}
                                />
                                {errors.price && <small className="p-error block mt-1">{errors.price}</small>}
                            </div>
                        )}
                        {isWarranty && isEdit && invoiceDetail?.dealerType === 1 && (
                            <div className="col-12 md:col-6">
                                <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.priceWithKDV')}</label>
                                <InputNumber value={formData.priceWithKDV ?? null} min={0} mode="decimal" minFractionDigits={2} maxFractionDigits={4} placeholder={th('fullServiceInvoiceKm.placeholders.priceWithKDV')} disabled />
                            </div>
                        )}

                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.currency')} *</label>
                            <InputText value={formData.currency} disabled className={errors.currency ? 'p-invalid' : ''} placeholder={th('fullServiceInvoiceKm.placeholders.currency')} />
                            {errors.currency && <small className="p-error block mt-1">{errors.currency}</small>}
                        </div>

                        <div className="col-12 md:col-6">
                            <label className="font-bold block mb-2">{th('fullServiceInvoiceKm.formLabels.description')}</label>
                            <InputText value={formData.description ?? ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder={th('fullServiceInvoiceKm.placeholders.description')} disabled={inputDisabled} />
                        </div>
                    </div>
                </div>
            </Dialog>

            {/* ReasonDialog (inline) */}
            <Dialog
                visible={reasonDlg.visible}
                header={cfg.title}
                onHide={handleReasonCancel}
                modal
                style={{ width: 520 }}
                closable={!isProcessing}
                dismissableMask={false}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label={th('fullServiceInvoiceKm.reasonDialog.cancel')} icon="pi pi-times" className="p-button-outlined" onClick={handleReasonCancel} disabled={isProcessing} />
                        <Button label={cfg.confirmButton} icon="pi pi-check" onClick={handleReasonConfirm} className={reasonDlg.type === 'reject' ? 'p-button-danger' : 'p-button-warning'} disabled={isProcessing} />
                    </div>
                }
            >
                <div style={{ position: 'relative' }}>
                    {isProcessing && (
                        <div style={overlayStyle}>
                            <ProgressSpinner />
                        </div>
                    )}

                    <div className="field">
                        <label htmlFor="reason" className="font-bold block mb-2">
                            {cfg.label} ({cfg.minLengthText})
                        </label>
                        <InputTextarea
                            id="reason"
                            value={reasonDlg.text}
                            onChange={(e) => setReasonDlg((p) => ({ ...p, text: e.target.value }))}
                            rows={5}
                            className={`${reasonDlg.submitted && (!reasonDlg.text || reasonDlg.text.trim().length < cfg.minLength) ? 'p-invalid' : ''} w-full`}
                            autoFocus
                            placeholder={cfg.placeholder}
                            disabled={isProcessing}
                        />
                        {reasonDlg.submitted && !reasonDlg.text && <small className="p-error">{cfg.requiredMessage}</small>}
                        {reasonDlg.submitted && reasonDlg.text && reasonDlg.text.trim().length < cfg.minLength && <small className="p-error">{cfg.minLengthMessage}</small>}
                        <div className="mt-2">
                            <small className="text-secondary">{characterCountText}</small>
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default FullServiceKmFormDialog;
